import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, Observable, of, throwError } from 'rxjs';
import { take, catchError, mergeMap, map, toArray, switchMap } from 'rxjs/operators';
import { AcceptDraftResponse, DraftList, ExpectedOwnedStrategy } from 'src/app/shared/owned-strategy-card/strategy';
import { UserService } from '../user/user.service';
import {
  ApiExpectedStrategy,
  InvestmentEdit,
  ApiModelStrategy,
  ModelStrategyOrPortfolioInstrumentOut,
  ApiClientAccountOut,
  ApiAvailableCash,
  ApiPagedTransaction,
  PaginatedTransactionInput,
  NewPortfolioInput,
  NotificationApiResponse,
  ApiNotificationsCount,
  PortfolioOut,
  ApiCalendar,
  ApiModelPortfolioOut,
  ModelPortfolioChartInput,
  PortfolioDepositOut,
  ApiPortfolioSummaryLiteOut,
  ApiSummaryChartOut,
  StrategyLimitOut,
  ClientBankAccountOut,
  WithdrawInput,
  SummaryChartInput,
  PagedResultDocumentOut,
  DocumentInput,
  ClientCollaboratorOut,
  AllInvestmentsListItem,
  ApiSummarySum,
  ApiUpdateStrategy,
  PageRequest,
  BlogArticlesPage,
  BlogArticleDetail,
  InstrumentPredictionOut,
  DraftPortfolioOut,
  InstrumentDetailFullOut,
  DraftStatusResponse,
  ManagementTypesEnum,
  ModelPortfolioInstruments,
  ClientAccountAdditionOut,
  ClientAccountAdditionNewIn,
  PushNotificationsRequestInput,
  PortfolioInstrumentSummaryOut,
  EntryFeeCalculationWithAllRatiosOut,
  EntryFeeCalculationIn,
  ClientRequestInput,
  PortfolioFeeConfigsOut,
  ApiModelPortfolioChartOut,
  AccessToken,
  investmentFrequencyMap,
  UpdatePortfolioV2Input,
  ApiDevicePlatforms,
  ValidVersionOut,
  ApiEfektikaUpdateStrategy,
  ModelStrategyChartInput,
  EfektikaActiveOut,
  ApiRegularWithdrawal,
  ApiWithdrawalPortfolio,
  ApiRegularWithdrawalLimit,
  RegularWithdrawalFrequencies,
  NewRegularWithdrawalInput,
  EmailVerificationOut,
  DraftWithdrawalAcceptIn,
  GenerateDocumentLinkOut,
  ExportTransactionsIn,
  RegularWithdrawalDraftsOut,
} from './rest-api';
import * as dayjs from 'dayjs';
import { ConfigService } from '../json-config.service';
import { EnvironmentKeys } from 'src/environments/environment-keys';
import { Router } from '@angular/router';
import { LoggedUser } from '../authentication/authentication';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import * as Sentry from '@sentry/angular';
import { desktopWidthBreakpoint } from 'src/app/shared/constants';
import { InstrumentCategories } from 'src/app/shared/charts/charts';

@Injectable({
  providedIn: 'root'
})
export class RestApiService {
  private get appHeaders() {
    return new HttpHeaders({
      'accept': 'application/json',
      'Authorization': 'Bearer ' + this.userService.bearer,
    });
  }

  private get loggedUser(): LoggedUser {
    if(!this.userService.loggedUser || this.userService.loggedUser === null) {
      this.router.navigate(['/login']);
    }

    return this.userService.loggedUser!;
  }

  private toHttpParams(paramsObj?: Record<string, unknown>): HttpParams {
    let params = new HttpParams();
    if(paramsObj) {
      Object.entries(paramsObj).forEach(([key, value]) => {
        if (value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => params = params.append(key, String(v)));
          } else {
            params = params.set(key, String(value));
          }
        }
      });
    }
    return params;
  }

  public constructor(
    private http: HttpClient,
    private userService: UserService,
    private configService: ConfigService,
    private router: Router,
    private sanitizer: DomSanitizer,
  ) { }

  // GET /api/v2/client/{clientAccountId}/summary
  public getSummaryWithPortfolios(params?: { clientId?: number, currencyCode?: string}): Observable<ApiSummarySum> {
    return this.http
      .get<ApiSummarySum>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v2/client/${params?.clientId ?? this.loggedUser.clientAccountId}/summary?currencyCode=${params?.currencyCode ?? this.loggedUser.currencyCode}`,
        {
          headers: this.appHeaders,
        },
      )
      .pipe(
        take(1),
        switchMap((summary: ApiSummarySum) => {
          summary.portfolios.forEach(
            (p) =>{
              p.weeksBetweenInvestments = investmentFrequencyMap.get(
                p.investmentFrequency
              )!;
              p.isDraft = false;
          });
          return of(summary);
        }),
        catchError(this.handleError.bind(this)),
      );
  }

  public getPortfoliosAndStrategies(params?: { clientId?: number, currencyCode?: string}): Observable<ExpectedOwnedStrategy[]> {
    return this.getSummaryWithPortfolios(params)
    .pipe(
      switchMap(res => of(res.portfolios))
    )
  }

  // GET /api/v1/client/{clientAccountId}/portfolio/summary-lite
  public getSummaryLite(): Observable<ApiPortfolioSummaryLiteOut[]> {
    return this.http
      .get<ApiPortfolioSummaryLiteOut[]>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/${this.loggedUser.clientAccountId}/portfolio/summary-lite?currencyCode=${this.loggedUser.currencyCode}`,
        {
          headers: this.appHeaders,
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientAccountId}/summary/graph
  public  getSummaryChart(params: SummaryChartInput): Observable<ApiSummaryChartOut> {
    const defaultTicks = window.innerWidth >= desktopWidthBreakpoint ? 100 : 25;

    return this.http
      .get<ApiSummaryChartOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/${params.clientId ?? this.loggedUser.clientAccountId}/summary/graph`,
        {
          headers: this.appHeaders,
          params: {
            currencyCode: params.currencyCode?? this.loggedUser.currencyCode,
            fromDate: params.fromDate?.toISOString().slice(0, 10) ?? '',
            toDate: params.toDate?.toISOString().slice(0, 10) ?? '',
            size: params.size ?? defaultTicks,
            priceMeasure: params.priceMeasure ?? 'VALUE',
          },
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/graph
  public getInvestmentChart(params: SummaryChartInput, portfolioId: string): Observable<ApiSummaryChartOut> {
    const defaultTicks = window.innerWidth >= desktopWidthBreakpoint ? 100 : 25;
    return this.http
      .get<ApiSummaryChartOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/${params.clientId ?? this.loggedUser.clientAccountId}/portfolio/${portfolioId}/graph`,
        {
          headers: this.appHeaders,
          params: {
            currencyCode: params.currencyCode ?? this.loggedUser.currencyCode,
            fromDate: params.fromDate?.toISOString().slice(0, 10) ?? '',
            toDate: params.toDate?.toISOString().slice(0, 10) ?? '',
            size: params.size ?? defaultTicks,
            priceMeasure: params.priceMeasure ?? 'VALUE',
          },
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/instrument/cash
  public getAvailableCash(): Observable<ApiAvailableCash> {
    return this.http
      .get<ApiAvailableCash>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/instrument/cash`,
        {
          headers: this.appHeaders,
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/calendar/date
  public getCalendar(from?: Date | dayjs.Dayjs, to?: Date | dayjs.Dayjs): Observable<ApiCalendar> {
    return this.http
      .get<ApiCalendar>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/calendar/date`,
        {
          headers: this.appHeaders,
          params: {
            fromDate: from?.toISOString().slice(0, 10) ?? '',
            toDate: to?.toISOString().slice(0, 10) ?? '',
          }
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/clientAccount/{clientAccountId}/frameworkContract/{frameworkContractId}/strategy
  public postStrategy(strategyData: ApiExpectedStrategy): Observable<{ portfolioId: number }> {
    Sentry.captureMessage('User POSTed attached new Strategy.', {
      level: 'info',
      extra: { strategyData },
    })
    return this.http
      .post<{ portfolioId: number }>(
        this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/clientAccount/' + this.loggedUser.clientAccountId+ '/frameworkContract/' + strategyData.frameworkContractId + '/strategy',
        {
          ...strategyData
        },
        {
          headers: this.appHeaders,
        }
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/clientAccount/{clientAccountId}/frameworkContract/{frameworkContractId}/portfolio
  public postPortfolio(portfolioData: NewPortfolioInput): Observable<{ portfolioId: number }> {
    Sentry.captureMessage('User POSTed attached new Portfolio.', {
      level: 'info',
      extra: { portfolioData },
    })
    return this.http
      .post<{ portfolioId: number }>(
        this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/clientAccount/'+ this.loggedUser.clientAccountId + '/frameworkContract/' + portfolioData.frameworkContractId +'/portfolio',
        {
          ...portfolioData,
          currencyCode: this.loggedUser.currencyCode,
        },
        {
          headers: this.appHeaders
        }
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/instrument/detail
  public getInstruments(canReadEnglishOverride?: boolean): Observable<InstrumentDetailFullOut[]> {
    return this.http
      .get<InstrumentDetailFullOut[]>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/instrument/detail`,
        {
          headers: this.appHeaders,
          params: {
            withIsInEnglish: canReadEnglishOverride ?? (this.loggedUser.canReadEnglish ?? false),
          }
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/clientAccount/{clientAccountId}/draftPortfolio
  public getClientDrafts(): Observable<DraftPortfolioOut[]> {
    return this.http
      .get<DraftPortfolioOut[]>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/clientAccount/${this.loggedUser.clientAccountId}/draftPortfolio`,
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/modelStrategy
  public getModelStrategies(overrideCanReadEnglish = false, efektika = false): Observable<ApiModelStrategy[]> {
    return this.http
      .get<ApiModelStrategy[]>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/modelStrategy`,
        {
          headers: this.appHeaders,
          params: {
            withIsInEnglish: overrideCanReadEnglish ?? (this.loggedUser.canReadEnglish ?? false),
            efektika
          }
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientAccountId}/modelStrategy
  public getModelStrategiesWithInstrumentsCategoriesFilter(): Observable<ApiModelStrategy[]> {
    return this.http
      .get<ApiModelStrategy[]>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/${this.loggedUser.clientAccountId}/modelStrategy`,
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientAccountId}/transaction
  public getTransactions(options?: PaginatedTransactionInput): Observable<ApiPagedTransaction> {
    if(options?.page) {
      options.page = options.page - 1; // api pages start at 0
    }

    if (options) {
      return this.http
        .get<ApiPagedTransaction>(
          `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/${options.clientId ?? this.loggedUser?.clientAccountId}/transaction`,
          {
            headers: this.appHeaders,
            params: this.toHttpParams(options),
          },
        ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
    } else {
      return this.http
        .get<ApiPagedTransaction>(
          `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/${this.loggedUser.clientAccountId}/transaction`,
          {
            headers: this.appHeaders,
          },
        ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
    }
  }

  // GET /api/v1/client/current
  public getUserDetail(): Observable<ApiClientAccountOut> {
    return this.http
      .get<ApiClientAccountOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/current`,
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientAccountId}/document
  public getDocuments(params?: DocumentInput): Observable<PagedResultDocumentOut> {
    return this.http
      .get<PagedResultDocumentOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/${this.loggedUser.clientAccountId}/document`,
        {
          headers: this.appHeaders,
          params: this.toHttpParams(params),
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/client/{clientAccountId}/document/{documentId}/link
  public generateDocumentLink(documentId: number): Observable<{link: string}> {
    return this.http
      .post<{link: string}>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/${this.loggedUser.clientAccountId}/document/${documentId}/link`,
        {
          resolveAll: true,
        },
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET api/v1/modelStrategy/{id}
  public getModelStrategy(id: string): Observable<ModelStrategyOrPortfolioInstrumentOut> {
    return this.http
      .get<ModelStrategyOrPortfolioInstrumentOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/modelStrategy/${id}`,
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientId}/web-notification/processed-sum
  public getNewNotificationsCount(): Observable<ApiNotificationsCount> {
    return this.http
      .get<ApiNotificationsCount>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/${this.loggedUser.clientAccountId}/web-notification/processed-sum`,
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/strategy/model-chart
  public getStrategyModelChart(options: ModelStrategyChartInput): Observable<ApiModelPortfolioChartOut> {
    return this.http
      .post<ApiModelPortfolioChartOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/strategy/model-chart`,
        options,
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/client/{clientId}/web-notification/{notificationWebId}/resolve
  public resolveNotification(id: number, resolveAll?: true): Observable<void> {
    return this.http
      .post<void>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/${this.loggedUser.clientAccountId}/web-notification/${id}/resolve`,
        {},
        {
          headers: this.appHeaders,
          params: { resolveAll: !!resolveAll }
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/client/{clientId}/modelPortfolio/{riskScore}/chart
  public modelPortfolioChart(params: ModelPortfolioChartInput): Observable<ApiModelPortfolioChartOut> {
    return this.http
      .post<ApiModelPortfolioChartOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/${this.loggedUser.clientAccountId}/modelPortfolio/${params.riskProfile}/chart`,
        {
          weeksBetweenInvestments: params.depositRegularity.value,
          yearsToInvest: params.timeOfInvestment,
          initialDeposit: params.oneTimeDeposit,
          regularDeposit: params.regularDeposit
        },
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/withdrawall
  public withdrawAll(portfolioId: number | string, bankAccountId: number): Observable<void> {
    return this.http
      .post<void>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/${this.loggedUser.clientAccountId}/portfolio/${portfolioId}/withdrawall`,
        {
          clientBankAccountId: bankAccountId,
        },
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/withdraw
  public withdrawInvestment(params: WithdrawInput): Observable<void> {
    return this.http
      .post<void>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/${this.loggedUser.clientAccountId}/portfolio/${params.portfolioId}/withdraw`,
        {
          clientBankAccountId: params.clientBankAccountId,
          amount: params.amount,
        },
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/device/create/{clientAccountId}
  public sendDeviceToken(options: PushNotificationsRequestInput): Observable<void> {
    return this.http.post<void>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/device/create/' + this.loggedUser.clientAccountId,
      {
        token: options.deviceToken,
        type: options.platform,
      },
      {
        headers: this.appHeaders,
      },
    )
    .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // DELETE /api/v1/device/delete/{clientAccountId}
  public deleteDeviceToken(options: PushNotificationsRequestInput): Observable<void> {
    return this.http
    .delete<void>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/device/delete/' + this.loggedUser.clientAccountId,
      {
        headers: this.appHeaders,
        body: {
          token: options.deviceToken,
          type: options.platform,
        }
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // GET /api/v1/client/{clientId}/portfolio/{portfolioId}/model-portfolio
  public getInvestmentDetail(params: ClientRequestInput): Observable<PortfolioOut> {
    return this.http
      .get<PortfolioOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/${params.clientId ?? this.loggedUser.clientAccountId}/portfolio/${params.portfolioId}/model-portfolio`,
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/deposit
  public getMoneyDeposit(params: ClientRequestInput): Observable<PortfolioDepositOut> {
    return this.http
      .get<PortfolioDepositOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/${params.clientId ?? this.loggedUser.clientAccountId}/portfolio/${params.portfolioId}/deposit`,
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v2/client/{clientAccountId}/portfolio/{portfolioId}/fee
  public getPortfolioFees(params: ClientRequestInput): Observable<PortfolioFeeConfigsOut> {
    return this.http
      .get<PortfolioFeeConfigsOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v2/client/${params.clientId ?? this.loggedUser.clientAccountId}/portfolio/${params.portfolioId}/fee`,
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/portfolio-instrument
  public getModelPortfolioInstruments(params: ClientRequestInput): Observable<PortfolioInstrumentSummaryOut> {
    return this.http
      .get<PortfolioInstrumentSummaryOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/${params?.clientId ?? this.loggedUser.clientAccountId}/portfolio/${params.portfolioId}/portfolio-instrument`,
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/strategy/limit
  public getStrategyLimits(): Observable<StrategyLimitOut> {
    return this.http
      .get<StrategyLimitOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/strategy/limit`,
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // todo - remove?
  public getInvestmentEdit(): InvestmentEdit {
    return {
      name: 'Na důchod',
      initialDeposit: 20000,
      regularDeposit: 5000,
      currencyCode: 'CZK',
      weeksBetweenInvestments: 4,
      investmentFrequency: 'CONTINUOUS',
      totalYearsToInvest: 5,
    };
  }

  // GET /api/v1/client/{clientId}/portfolio/{portfolioId}/bank-account
  public getAvailableBankAccounts(portfolioId: number | string): Observable<ClientBankAccountOut[]> {
    return this.http
      .get<ClientBankAccountOut[]>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/${this.loggedUser.clientAccountId}/portfolio/${portfolioId}/bank-account`,
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientId}/web-notification
  public getAllNotifications(type: 'MESSAGE' | 'NOTICE', page = 0): Observable<NotificationApiResponse> {
    return this.http
    .get<NotificationApiResponse>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/client/' + this.loggedUser.clientAccountId + '/web-notification',
      {
        headers: this.appHeaders,
        params: {
          page,
          notificationKind: type,
        }
      },
    )
    .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientId}/modelPortfolio/{riskScore}
  public getModelPortfolio(riskScore: number): Observable<ApiModelPortfolioOut> {
    return this.http
    .get<ApiModelPortfolioOut>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/client/' + this.loggedUser.clientAccountId + '/modelPortfolio/' + riskScore,
      {
        headers: this.appHeaders,
      },
    )
    .pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // GET /api/v1/client/{clientAccountId}/regular-withdrawal
  public getRegularWithdrawals(): Observable<ApiRegularWithdrawal[]> {
    return this.http
      .get<ApiRegularWithdrawal[]>(
        this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/client/' + this.loggedUser.clientAccountId + '/regular-withdrawal',
        {
          headers: this.appHeaders,
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // DELETE /api/v1/client/{clientAccountId}/regular-withdrawal/{regularWithdrawalId}
  public cancelRegularWithdrawal(regularWithdrawalId: number): Observable<void> {
    return this.http
      .delete<void>(
        this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/client/' + this.loggedUser.clientAccountId + '/regular-withdrawal/' + regularWithdrawalId,
        {
          headers: this.appHeaders,
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientAccountId}/regular-withdrawal/portfolio
  public getWithdrawalPortfolios(): Observable<ApiWithdrawalPortfolio[]> {
    return this.http
    .get<ApiWithdrawalPortfolio[]>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/client/' + this.loggedUser.clientAccountId + '/regular-withdrawal/portfolio',
      {
        headers: this.appHeaders,
      },
    )
    .pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // GET /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/regular-withdrawal/limit
  public getWithdrawalLimits(portfolioId: number, frequency: RegularWithdrawalFrequencies): Observable<ApiRegularWithdrawalLimit> {
    return this.http
      .get<ApiRegularWithdrawalLimit>(
        this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/client/' + this.loggedUser.clientAccountId + '/portfolio/' + portfolioId + '/regular-withdrawal/limit',
        {
          headers: this.appHeaders,
          params: {
            frequency
          }
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/client/{clientAccountId}/regular-withdrawal
  public createRegularWithdrawal(input: NewRegularWithdrawalInput): Observable<void> {
    return this.http
    .post<void>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/client/' + this.loggedUser.clientAccountId + '/regular-withdrawal',
      {
        ...input,
      },
      {
        headers: this.appHeaders,
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // GET /api/v1/modelPortfolio/{riskScore}
  public getModelPortfolioPublic(riskScore: number): Observable<ApiModelPortfolioOut> {
    return this.http
    .get<ApiModelPortfolioOut>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/modelPortfolio/' + riskScore,
      {
        headers: this.appHeaders,
      },
    )
    .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{id}/collaborator/info
  public getUserConsult(): Observable<ClientCollaboratorOut> {
    return this.http
    .get<ClientCollaboratorOut>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/client/' + this.loggedUser.clientAccountId + '/collaborator/info',
      {
        headers: this.appHeaders,
      },
    ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
    );
  }

  // GET /api/v1/client/{id}/collaborator/photo
  public getUserConsultImage(): Observable<Blob> {
    return this.http
    .get<Blob>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/client/' + this.loggedUser.clientAccountId + '/collaborator/photo',
      {
        responseType: 'blob' as 'json',
        headers: new HttpHeaders({
          'accept': 'image/jpeg',
          'Authorization': 'Bearer ' + this.userService.bearer,
        }),
      },
    ).pipe(
        take(1),
    );
  }


  // GET /api/v1/client/{id}/draftPortfolio/{draftPortfolioId}/collaborator-photo
  public getConsultImageByClientIdAndDraftPortfolioId(draftPortfolioId: number): Observable<Blob> {
    return this.http
    .get<Blob>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/client/' + this.loggedUser.clientAccountId + '/draftPortfolio/' + draftPortfolioId + '/collaborator-photo',
      {
        responseType: 'blob' as 'json',
        // observe: 'response',
        headers: new HttpHeaders({
          'accept': 'image/jpeg',
          'Authorization': 'Bearer ' + this.userService.bearer,
        }),
      },
    ).pipe(
        take(1),
    );
  }

  // GET /api/v1/client/{clientAccountId}/portfolio/all
  public getAllInvestments(): Observable<AllInvestmentsListItem[]> {
    return this.http
    .get<AllInvestmentsListItem[]>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/client/' + this.loggedUser.clientAccountId + '/portfolio/all',
      {
        headers: this.appHeaders,
      },
    ).pipe(
        take(1),
    );
  }

  // POST /api/v1/temporary-document/transaction/export
  public generateTransactionsExport(params: ExportTransactionsIn): Observable<GenerateDocumentLinkOut> {
    return this.http
    .post<GenerateDocumentLinkOut>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/temporary-document/transaction/export',
      {},
      {
        headers: this.appHeaders,
        params: { ...params },
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // POST /api/v1/client/{id}/collaborator/meeting
  public requestConsultMeeting(): Observable<void> {
    return this.http
    .post<void>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/client/' + this.loggedUser.clientAccountId + '/collaborator/meeting',
      {},
      {
        headers: this.appHeaders,
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // POST /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/temporary-document/employer-payment
  public employerPaymentPdf(params: ClientRequestInput): Observable<{ link: string }> {
    return this.http
    .post<{ link: string }>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/client/' + (params.clientId ?? this.loggedUser.clientAccountId) + '/portfolio/' + params.portfolioId + '/temporary-document/employer-payment',
      {},
      {
        headers: this.appHeaders,
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // POST /api/v1/approveDipWithdrawal/{token}
  public approveDipWithdrawal(token: string): Observable<void> {
    return this.http
    .post<void>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/approveDipWithdrawal/' + token,
      {},
      {
        headers: this.appHeaders,
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }


  // PUT /api/v1/clientAccount/{clientAccountId}/portfolio/{portfolioId}
  public updateInvestment(updatedInvestment: { name: string , portfolioId: number | string }): Observable<{ newPortfolioName: string }> {
    return this.http
    .put<{ newPortfolioName: string }>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/clientAccount/' + this.loggedUser.clientAccountId + '/portfolio/' + updatedInvestment.portfolioId,
      {
        newPortfolioName: updatedInvestment.name,
      },
      {
        headers: this.appHeaders,
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // PUT /api/v2/clientAccount/{clientAccountId}/portfolio/{portfolioId}
  public updateInvestmentV2(input: UpdatePortfolioV2Input, portfolioId: number): Observable<void> {
    return this.http
    .put<void>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v2/clientAccount/' + this.loggedUser!.clientAccountId + '/portfolio/' + portfolioId,
      input,
      {
        headers: this.appHeaders,
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

    // PUT api/v1/clientAccount/{clientAccountId}/strategy/{strategyId}
  public updateStrategy(strategyUpdate: ApiUpdateStrategy, strategyId: number): Observable<void> {
    return this.http
    .put<void>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/clientAccount/' + this.loggedUser.clientAccountId + '/strategy/' + strategyId,
      strategyUpdate,
      {
        headers: this.appHeaders,
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // PUT api/v1/clientAccount/{clientAccountId}/efektika/{portfolioId}
  public updateEfektikaStrategy(strategyUpdate: ApiEfektikaUpdateStrategy, strategyId: number): Observable<void> {
    return this.http
    .put<void>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/clientAccount/' + this.loggedUser.clientAccountId + '/efektika/' + strategyId,
      strategyUpdate,
      {
        headers: this.appHeaders,
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // DELETE /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/close
  public deleteInvestment(portfolioId: string | number): Observable<void> {
    return this.http
    .delete<void>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/client/' + this.loggedUser.clientAccountId + '/portfolio/' + portfolioId + '/close',
      {
        headers: this.appHeaders,
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // GET /api/v1/client/{clientId}/frameworkContract/{frameworkContractId}/bank-account
  public getContractsBankAccounts(frameworkContractId: string | number): Observable<ClientBankAccountOut[]> {
    return this.http
    .get<ClientBankAccountOut[]>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/client/' + this.loggedUser.clientAccountId + '/frameworkContract/' + frameworkContractId + '/bank-account',
      {
        headers: this.appHeaders,
      },
    ).pipe(
        take(1),
    );
  }

  // POST /api/v1/strategy/backcasting-chart
  public getNewInvestmentHistoryChart(instrumentValues: Record<string, number>): Observable<InstrumentPredictionOut> {
    return this.http
    .post<InstrumentPredictionOut>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/strategy/backcasting-chart',
      {
        isinToWeightMap: instrumentValues,
      },
      {
        headers: this.appHeaders,
      },
    ).pipe(
        take(1),
    );
  }

  // POST /api/v1/framework-contract-addition/{uuid}
  public signContractAddition(token: string): Observable<void> {
    return this.http
    .post<void>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/framework-contract-addition/' + token,
      {},
      {
        headers: this.appHeaders,
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // POST /api/v1/email-verification/confirm/{token}
  public signEmail(token: string): Observable<EmailVerificationOut> {
    return this.http
    .post<EmailVerificationOut>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/email-verification/confirm/' + token,
      {},
      {
        headers: this.appHeaders,
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // POST /api/v1/client/{id}/can-read-english
  public canReadEnglish(): Observable<void> {
    return this.http
    .post<void>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/client/' + this.loggedUser.clientAccountId + '/can-read-english',
      {},
      {
        headers: this.appHeaders,
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // POST /api/v1/client/{clientAccountId}/framework-contract-addition/{frameworkContractAdditionId}
  public generateAdditionSignMail(frameworkId: number): Observable<void> {
    return this.http
    .post<void>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/client/' + this.loggedUser.clientAccountId + '/framework-contract-addition/' + frameworkId,
      {},
      {
        headers: this.appHeaders,
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // POST /api/v1/security/loginBCAS-mobile
  public backendLogin(code: string, redirectUri: string): Observable<AccessToken> {
    return this.http
    .post<AccessToken>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/security/loginBCAS-mobile',
      {
        code,
        redirectUri
      },
    )
    .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/security/loginBCAS-web
  public backendWebLogin(code: string, redirectUri: string): Observable<AccessToken> {
    return this.http
    .post<AccessToken>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/security/loginBCAS-web',
      {
        code,
        redirectUri
      },
    )
    .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/client/{id}/disclaimer-shown
  public setDisclaimerShown(clientAccountId: number): Observable<void> {
    return this.http
    .post<void>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/client/' + clientAccountId + '/disclaimer-shown',
      {},
      {
        headers: this.appHeaders,
      }
    )
    .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/blog
  public getBlogArticlesPage(pageRequest: PageRequest): Observable<BlogArticlesPage> {
    return this.http
    .get<BlogArticlesPage>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/blog',
      {
        headers: this.appHeaders,
        params: this.toHttpParams(pageRequest),
      },
    ).pipe(
        take(1),
    );
  }

  // GET /api/v1/blog/{articleId}
  public getBlogArticleDetail(articleId: number): Observable<BlogArticleDetail> {
    return this.http
    .get<BlogArticleDetail>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/blog/' + articleId,
      {
        headers: this.appHeaders,
      },
    ).pipe(
        take(1),
    );
  }

  // GET /api/v1/blog/preview (unauthorized)
  public getBlogArticlePreview(token: string): Observable<BlogArticleDetail> {
    return this.http
    .get<BlogArticleDetail>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/blog/preview',
      {
        params: { token }
      },
    ).pipe(
        take(1)
    );
  }

  // GET /api/v1/clientAccount/{clientAccountId}/draftPortfolio/{draftPortfolioId}/status
  public getDraftPortfolioStatus(draftPortfolioId: number): Observable<DraftStatusResponse> {
    return this.http
    .get<DraftStatusResponse>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/clientAccount/' + this.loggedUser.clientAccountId + '/draftPortfolio/' + draftPortfolioId + '/status',
      {
        headers: this.appHeaders,
      },
    ).pipe(
        take(1)
    );
  }

  // GET /api/v1/client/{clientAccountId}/addition
  public getClientAdditions(): Observable<ClientAccountAdditionOut[]> {
    return this.http
    .get<ClientAccountAdditionOut[]>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/client/' + this.loggedUser.clientAccountId + '/addition',
      {
        headers: this.appHeaders,
      },
    ).pipe(
        take(1)
    );
  }

  // GET /api/v1/client/{clientAccountId}/regular-withdrawal-draft
  public getRegularWithdrawalDrafts(): Observable<RegularWithdrawalDraftsOut> {
    return this.http
    .get<RegularWithdrawalDraftsOut>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/client/' + this.loggedUser.clientAccountId + '/regular-withdrawal-draft',
      {
        headers: this.appHeaders,
      },
    ).pipe(
        take(1)
    );
  }

  // POST /api/v1/client/{clientAccountId}/regular-withdrawal-draft/{regularWithdrawalDraftId}/reject
  public rejectWithdrawalDraft(regularWithdrawalDraftId: string | number): Observable<void> {
    return this.http
      .post<void>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/${this.loggedUser.clientAccountId}/regular-withdrawal-draft/${regularWithdrawalDraftId}/reject`,
        {},
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/regular-withdrawal-draft/{regularWithdrawalDraftId}/accept
  public acceptWithdrawalDraft(input: DraftWithdrawalAcceptIn): Observable<void> {
    return this.http
      .post<void>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/${this.loggedUser.clientAccountId}/portfolio/${input.portfolioId}/regular-withdrawal-draft/${input.regularWithdrawalDraftId}/accept`,
        {},
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/client/{clientAccountId}/addition
  public createClientAddition(type: ClientAccountAdditionNewIn): Observable<void> {
    return this.http
      .post<void>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client/${this.loggedUser.clientAccountId}/addition`,
        {
          type
        },
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/client-account-addition/{uuid}
  public signClientAddition(token: string): Observable<void> {
    return this.http
      .post<void>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/client-account-addition/${token}`,
        {},
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  private getCanAcceptDraft(draftPortfolioType: 'PORTFOLIO' | 'STRATEGY', mpis?: ModelPortfolioInstruments[], draftRiskScore?: number): boolean {
    if (draftPortfolioType === 'STRATEGY') {
      return this.loggedUser?.managementType === ManagementTypesEnum.EXECUTION_ONLY && this.getStrategyInstrumentsAllowed(mpis);
    } else if (draftPortfolioType === 'PORTFOLIO') {
      return !!draftRiskScore && draftRiskScore <= this.loggedUser?.maxRiskScore;
    }
    return false;
  }

  private getStrategyInstrumentsAllowed(mpis?: ModelPortfolioInstruments[]): boolean {
    if (mpis) {
      try {
        const allowedCats = this.loggedUser?.instrumentCategories;
        const instrumentsCats = [...new Set(mpis.map(mpi => mpi.instrument.instrumentCategory))] as InstrumentCategories[];
        if (allowedCats?.length && instrumentsCats?.length) {
          return instrumentsCats.every((ic) => allowedCats.includes(ic));
        }
      } catch (error) {
        console.error('Error ocurred while checking if strategy draft instrument is allowed', error);
        return false;
      }
    }

    return false;
  }

  // GET /api/v1/clientAccount/{clientAccountId}/draftPortfolio
  public getClientDraftPortfoliosAndStrategies(): Observable<ExpectedOwnedStrategy[]> {
    return this.http
      .get<DraftList>(
        `${this.configService.getConfig(
          EnvironmentKeys.apiUrl,
        )}${this.configService.getConfig(
          EnvironmentKeys.apiVersion,
        )}/clientAccount/${this.loggedUser.clientAccountId}/draftPortfolio`,
        {
          headers: this.appHeaders,
        },
      )
      .pipe(
        take(1),
        mergeMap((draftsList: DraftList) => from(draftsList.draftPortfolios.map(
          draft => ({
            ...draft,
            aml: draftsList.aml,
            isDraft: true,
            canAcceptDraft: this.getCanAcceptDraft(
              draft.portfolioType, draft.modelPortfolioInstruments, draft.riskScore
            )
          })
        ))),
        // For each draft, if it has collaborator associated, attempt to fetch their photo
        mergeMap((draft: ExpectedOwnedStrategy) =>
          draft?.draftPortfolioId && draft?.collaborator?.hasPhoto
            ? this.getConsultImageByClientIdAndDraftPortfolioId(
              draft?.draftPortfolioId
              ).pipe(
                // On success convert image blob to URL format
                map((photo) => ({
                  ...draft,
                  consultantPhoto: this.getPhotoUrlFromBlob(photo),
                })),
                // On error while fetching photo, return draft with the silhouette img
                catchError(() => of(({...draft, consultantPhoto: '/assets/images/silhouette.png' }))),
              )
            // If no collaborator, set the silhouette img
            : of(({...draft, consultantPhoto: '/assets/images/silhouette.png' }))
        ),
        toArray(),
        catchError(this.handleError.bind(this)),
      );
  }

  private getPhotoUrlFromBlob(photo: Blob): SafeUrl {
      const photoUrl = photo
      ? this.sanitizer.bypassSecurityTrustUrl(
          URL.createObjectURL(photo),
        )
      : '/assets/images/silhouette.png';
      return photoUrl;
  }

  // POST /api/v1/clientAccount/{clientAccountId}/draftPortfolio/{draftPortfolioId}/accept
  public acceptDraftPortfolio(draftPortfolioId: number): Observable<AcceptDraftResponse> {
    return this.http
      .post<AcceptDraftResponse>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/clientAccount/${this.loggedUser.clientAccountId}/draftPortfolio/${draftPortfolioId}/accept`,
        {},
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/clientAccount/{clientAccountId}/draftPortfolio/{draftPortfolioId}/refuse
  public refuseDraftPortfolio(draftPortfolioId: number): Observable<null> {
    return this.http
      .post<null>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}${this.configService.getConfig(EnvironmentKeys.apiVersion)}/clientAccount/${this.loggedUser.clientAccountId}/draftPortfolio/${draftPortfolioId}/refuse`,
        {},
        {
          headers: this.appHeaders,
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/fee-calculation
  public calculateEntryFeeForNewInvestment(calcInput: EntryFeeCalculationIn): Observable<EntryFeeCalculationWithAllRatiosOut> {
    return this.http
    .post<EntryFeeCalculationWithAllRatiosOut>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/fee-calculation',
      calcInput,
      {
        headers: this.appHeaders,
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // POST /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/entry-fee
  public calculateEntryFeeForPortfolio(calcInput: EntryFeeCalculationIn, portfolioId: number): Observable<EntryFeeCalculationWithAllRatiosOut> {
    return this.http
    .post<EntryFeeCalculationWithAllRatiosOut>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + `v1/client/${this.loggedUser.clientAccountId}/portfolio/${portfolioId}/entry-fee`,
      calcInput,
      {
        headers: this.appHeaders,
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // POST /api/v1/security/refresh
  public backendRefreshAuth(refreshToken: string): Observable<AccessToken> {
    return this.http
    .post<AccessToken>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/security/refresh',
      {
        refreshToken
      },
    )
    .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/security/logout
  public logoutBackend(): Observable<{ idToken: string }> {
    return this.http
    .get<{ idToken: string }>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + this.configService.getConfig(EnvironmentKeys.apiVersion) + '/security/logout',
      {
        headers: this.appHeaders,
      },
    ).pipe(take(1));
  }

  // GET /api/v1/mobile/validate-version
  public validatePhoneAppVersion(
    deviceType: ApiDevicePlatforms, version: string
  ): Observable<ValidVersionOut> {
    return this.http
    .get<ValidVersionOut>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/mobile/validate-version',
      {
        headers: this.appHeaders,
        params: {
          device: deviceType, version
        }
      },
    ).pipe(
        take(1)
    );
  }

  // GET /api/v1/clientAccount/{clientAccountId}/efektika/active
  public efektikaValidSince(): Observable<EfektikaActiveOut> {
    return this.http
    .get<EfektikaActiveOut>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + `v1/clientAccount/${this.loggedUser.clientAccountId}/efektika/active`,
      {
        headers: this.appHeaders,
      },
    ).pipe(
        take(1)
    );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    return throwError(() => error);
  }
}
